import React from "react";
import Reveal from "./Reveal";

/**
 * Momentum / Roadmap — a horizontal-rhythm timeline that signals pace.
 * Mistral / OpenAI / Anthropic all use this pattern: a tight, dated list
 * showing the company is moving faster than the market.
 */
const MILESTONES = [
  {
    when: "APR 2026",
    title: "Methodology codified",
    body: "The Practitioner Method™ formalized across 3 tiers, 6 pillars, 4 audiences.",
    status: "shipped",
  },
  {
    when: "MAY 2026",
    title: "Inaugural Arena · HIT",
    body: "1,000+ attended. 70 pods built real AI products. 15 builders being backed.",
    status: "shipped",
  },
  {
    when: "Q3 2026",
    title: "Main platform debuts",
    body: "Certifications, AI Jobs marketplace, Pitch Hub, community — unified.",
    status: "next",
  },
  {
    when: "Q4 2026",
    title: "Global December Finale",
    body: "Regional Arena winners compete on one stage. One champion cohort. Real backing.",
    status: "next",
  },
  {
    when: "2027",
    title: "50+ Arena editions worldwide",
    body: "South Asia, GCC, Southeast Asia, Africa. Built in India. Made for the world.",
    status: "horizon",
  },
];

const STATUS_LABEL = {
  shipped: { text: "SHIPPED", color: "var(--accent-gold)" },
  next: { text: "NEXT", color: "var(--accent-primary)" },
  horizon: { text: "ON THE HORIZON", color: "var(--ink-tertiary)" },
};

export default function Momentum() {
  return (
    <section
      id="momentum"
      className="py-10 md:py-14 border-t hairline"
      style={{ background: "var(--canvas-deep)" }}
      data-testid="momentum-section"
    >
      <div className="container-edge">
        <Reveal>
          <p className="section-number">MOMENTUM · ROADMAP</p>
        </Reveal>
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7">
            <Reveal>
              <h2
                className="font-display text-[36px] sm:text-[44px] lg:text-[56px] leading-[1.0] tracking-tight"
                style={{ fontWeight: 500 }}
              >
                Where we are. <em className="italic grad-brand-text" style={{ fontWeight: 500 }}>Where we're going.</em>
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-5 flex items-end">
            <Reveal delay={120}>
              <p
                className="text-[15px] md:text-[17px] leading-relaxed"
                style={{ color: "var(--ink-secondary)" }}
              >
                The AI shift is moving in months, not years. So are we. Here's what we've
                shipped, what's next, and what's on the horizon — across the next 18 months.
              </p>
            </Reveal>
          </div>
        </div>

        <div className="mt-12 relative">
          {/* Vertical spine */}
          <div
            className="absolute left-[14px] md:left-[18px] top-2 bottom-2 w-px"
            style={{
              background:
                "linear-gradient(180deg, var(--accent-gold) 0%, var(--accent-primary) 60%, transparent 100%)",
            }}
            aria-hidden="true"
          />

          <ol className="space-y-7">
            {MILESTONES.map((m, i) => {
              const s = STATUS_LABEL[m.status];
              return (
                <Reveal key={m.title} delay={i * 60} as="li" className="relative pl-12 md:pl-16">
                  {/* Node */}
                  <span
                    className="absolute left-0 top-1 w-7 h-7 md:w-9 md:h-9 rounded-full grid place-items-center"
                    style={{
                      background: "var(--canvas-deep)",
                      border: "1px solid var(--border-strong)",
                      boxShadow:
                        m.status === "shipped"
                          ? "0 0 0 4px rgba(201,169,97,0.18)"
                          : m.status === "next"
                          ? "0 0 0 4px rgba(79,70,229,0.16)"
                          : "none",
                    }}
                    aria-hidden="true"
                  >
                    <span
                      className="w-2 h-2 md:w-2.5 md:h-2.5 rounded-full"
                      style={{ background: s.color }}
                    />
                  </span>

                  <div className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-6 pt-1">
                    <div className="md:col-span-3">
                      <p
                        className="font-mono text-[12px]"
                        style={{ color: "var(--ink)", letterSpacing: "0.16em" }}
                      >
                        {m.when}
                      </p>
                      <p
                        className="mt-1 font-mono text-[10px]"
                        style={{ color: s.color, letterSpacing: "0.18em" }}
                      >
                        {s.text}
                      </p>
                    </div>
                    <div className="md:col-span-9">
                      <h3
                        className="font-display text-[22px] md:text-[26px] tracking-tight leading-tight"
                        style={{ fontWeight: 500 }}
                      >
                        {m.title}
                      </h3>
                      <p
                        className="mt-2 text-[14.5px] leading-relaxed"
                        style={{ color: "var(--ink-secondary)" }}
                      >
                        {m.body}
                      </p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </ol>
        </div>
      </div>
    </section>
  );
}
